// ── /gig — New Gig quick-add ─────────────────────────────────────────────────
// One screen, home-screen-shortcut fast: title + date and you're done; every
// other field is an optional blank. Writes ONE event to the internal-ops
// calendar (HOLD-prefixed by default) with the labeled scaffold in the
// description (CONTACT:/RATE:/NOTES:) that the loose-ends parser reads back.
// This is a convenience for Dan's existing habit, never a requirement —
// typing straight into the GCal app keeps working identically.
import { useState } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { STAFF_THEME as T, FONT } from "../../theme/staff";

function chicagoToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());
}

export default function NewGig({ navigate }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(chicagoToday());
  const [startTime, setStartTime] = useState("");
  const [durationMin, setDurationMin] = useState(120);
  const [hold, setHold] = useState(true);
  const [contact, setContact] = useState("");
  const [rate, setRate] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // {htmlLink}

  async function save() {
    if (!title.trim()) { setError("Give it a name — even a rough one."); return; }
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "createGigEvent");
      const res = await fn({
        title: title.trim(), date, startTime: startTime || null,
        durationMin: Number(durationMin), hold, contact, rate, notes, address,
      });
      setDone({ htmlLink: res.data.htmlLink });
    } catch (e) {
      setError(e.message || "Couldn't create the event");
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setTitle(""); setDate(chicagoToday()); setStartTime(""); setDurationMin(120);
    setHold(true); setContact(""); setRate(""); setAddress(""); setNotes("");
    setDone(null); setError(null);
  }

  const input = {
    width: "100%", boxSizing: "border-box", background: T.raised, color: T.text,
    border: `1px solid ${T.border}`, borderRadius: "10px", padding: "0.8rem 0.9rem",
    fontSize: "1rem", fontFamily: FONT.body,
  };
  const label = { color: T.muted, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", margin: "0.9rem 0 0.3rem", display: "block" };

  if (done) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: FONT.body, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
        <div style={{ textAlign: "center", maxWidth: "420px" }}>
          <div style={{ fontSize: "3rem" }}>🥞</div>
          <h2 style={{ color: T.gold, fontFamily: FONT.display, fontWeight: 800, margin: "0.5rem 0" }}>
            {hold ? "Hold placed." : "Gig on the calendar."}
          </h2>
          <p style={{ color: T.muted, fontSize: "0.95rem", lineHeight: 1.5 }}>
            It's on internal operations{hold ? " — the assistant will nudge you to confirm or release it when the date gets close." : "."}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "1.25rem" }}>
            {done.htmlLink && (
              <a href={done.htmlLink} target="_blank" rel="noreferrer" style={{ background: T.raised, color: T.sky, border: `1px solid ${T.border}`, borderRadius: "10px", padding: "0.85rem", textDecoration: "none", fontWeight: 700, fontFamily: FONT.display }}>
                Open in Google Calendar →
              </a>
            )}
            <button onClick={reset} style={{ background: T.gold, color: T.bg, border: "none", borderRadius: "10px", padding: "0.85rem", fontWeight: 700, fontFamily: FONT.display, fontSize: "1rem", cursor: "pointer" }}>
              + Add another
            </button>
            <button onClick={() => navigate("/menu")} style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border}`, borderRadius: "10px", padding: "0.85rem", fontWeight: 700, fontFamily: FONT.display, fontSize: "0.9rem", cursor: "pointer" }}>
              ← Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: FONT.body, padding: "1rem 1rem 3rem" }}>
      <div style={{ maxWidth: "480px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0 0.5rem" }}>
          <h1 style={{ color: T.gold, fontFamily: FONT.display, fontSize: "1.35rem", fontWeight: 800, margin: 0, flex: 1 }}>
            🥞 New gig
          </h1>
          <button onClick={() => navigate("/menu")} style={{ background: "transparent", color: T.muted, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "0.4rem 0.7rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>
            ← Menu
          </button>
        </div>
        <p style={{ color: T.muted, fontSize: "0.85rem", margin: "0 0 0.5rem", lineHeight: 1.45 }}>
          Name + date is enough. Everything else is a blank you can fill now or never.
        </p>

        <label style={label}>What is it?</label>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Birthday pop-up for Sarah…" style={input} />

        <div style={{ display: "flex", gap: "0.6rem" }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Start (optional)</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={input} />
          </div>
        </div>

        {startTime && (
          <>
            <label style={label}>How long?</label>
            <select value={durationMin} onChange={(e) => setDurationMin(e.target.value)} style={input}>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
              <option value={360}>6 hours</option>
            </select>
          </>
        )}

        <button onClick={() => setHold(!hold)} style={{
          width: "100%", marginTop: "1rem", background: hold ? "rgba(252,179,31,0.15)" : T.raised,
          border: `1px solid ${hold ? T.gold : T.border}`, color: hold ? T.gold : T.muted,
          borderRadius: "10px", padding: "0.85rem", fontWeight: 700, fontFamily: FONT.display,
          fontSize: "0.95rem", cursor: "pointer",
        }}>
          {hold ? "🎟 Just a hold for now — nudge me later" : "✓ It's confirmed — no nudge needed"}
        </button>

        <label style={label}>Contact (optional)</label>
        <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Name, phone, or email" style={input} />

        <label style={label}>Rate discussed (optional)</label>
        <input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="$450 flat, $150/hr…" style={input} />

        <label style={label}>Where (optional)</label>
        <AddressAutocomplete value={address} onChange={setAddress} placeholder="Venue or address" />

        <label style={label}>Notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything you'll want to remember" style={{ ...input, resize: "vertical" }} />

        {error && (
          <div style={{ background: "rgba(239,83,126,0.15)", border: `1px solid ${T.pink}`, borderRadius: "10px", padding: "0.8rem", marginTop: "1rem", fontSize: "0.9rem" }}>
            {error}
          </div>
        )}

        <button disabled={saving} onClick={save} style={{
          width: "100%", marginTop: "1.25rem", background: T.gold, color: T.bg, border: "none",
          borderRadius: "12px", padding: "1rem", fontWeight: 800, fontFamily: FONT.display,
          fontSize: "1.1rem", cursor: "pointer", opacity: saving ? 0.6 : 1,
        }}>
          {saving ? "Placing it…" : hold ? "Place the hold" : "Put it on the calendar"}
        </button>
      </div>
    </div>
  );
}
